import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

declare const gapi: any;
declare const google: any;

export interface DrivePickerFile {
  id: string;
  name: string;
  mimeType: string;
  /** OAuth access token obtained from google.accounts.oauth2; forwarded to the backend for the download job. */
  accessToken: string;
}

@Injectable()
export class GoogleDrivePickerService {
  private gapiLoaded = false;

  /** Ensures the gapi script has been bootstrapped and the `picker` library is ready. */
  private loadGapi(): Promise<void> {
    if (this.gapiLoaded) return Promise.resolve();

    return new Promise((resolve, reject) => {
      if (typeof gapi === 'undefined') {
        reject(new Error('gapi is not available. Ensure the Google API script is loaded.'));
        return;
      }
      gapi.load('picker', {
        callback: () => {
          this.gapiLoaded = true;
          resolve();
        },
        onerror: () => reject(new Error('Failed to load Google Picker API.')),
      });
    });
  }

  /**
   * Request a short-lived OAuth2 access token from Google Identity Services
   * with the `drive.file` scope (grants access to files opened/selected via Picker).
   */
  private requestAccessToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (typeof google === 'undefined' || !google.accounts?.oauth2) {
        reject(new Error('Google Identity Services not available.'));
        return;
      }

      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: environment.googleClientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: (response: any) => {
          if (response.error) {
            reject(new Error(`OAuth error: ${response.error}`));
          } else {
            resolve(response.access_token as string);
          }
        },
      });

      tokenClient.requestAccessToken({ prompt: '' });
    });
  }

  /**
   * Opens the Google Picker dialog and returns the file the user selected
   * (including the OAuth access token for backend download), or `null` on cancel.
   */
  async open(): Promise<DrivePickerFile | null> {
    await this.loadGapi();
    const accessToken = await this.requestAccessToken();

    return new Promise((resolve) => {
      const view = new google.picker.DocsView()
        .setSelectFolderEnabled(false);

      const appId = environment.googleClientId.split('-')[0];

      const picker = new google.picker.PickerBuilder()
        .setAppId(appId)
        .addView(view)
        .setOAuthToken(accessToken)
        .setCallback((data: any) => {
          if (data.action === google.picker.Action.PICKED) {
            const doc = data.docs?.[0];
            if (doc) {
              resolve({
                id: doc.id,
                name: doc.name,
                mimeType: doc.mimeType,
                accessToken,
              });
            } else {
              resolve(null);
            }
          } else if (
            data.action === google.picker.Action.CANCEL ||
            data.action === google.picker.Action.ERROR
          ) {
            resolve(null);
          }
        })
        .build();

      picker.setVisible(true);
    });
  }
}
